import { useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { Icon } from '@dcc-erp/ui';

import { downloadXlsx, fileToGrid, type CellValue } from '../lib/xlsx';

// Generic xlsx/csv import dialog shared by every accounting page's "นำเข้า"
// button. Lifted from the original /coa import modal (routes/coa.tsx) and made
// data-agnostic so AR / AP / fixed assets / journal etc. all get the exact same
// pick → preview → running → done flow and look. A page supplies:
//   • fields/template      — the reference table + downloadable template
//   • parse(grid)          — grid → validated import units (one unit = one record;
//                            for multi-line docs one unit = a whole voucher)
//   • previewColumns       — what to show per unit before committing
//   • runImport(valid, …)  — POST each unit, reporting progress + a result tally

// One row of the field-reference table on the pick stage. Mirrors COA_CSV_FIELDS.
export interface ImportFieldSpec {
  col: string;
  label: string;
  required: boolean;
  hint: string;
}

// A parsed import unit. For flat masters one unit = one spreadsheet row; for
// multi-line documents (journal/ar/ap) one unit = a whole voucher + its lines.
export interface ParsedUnit<T> {
  // 1-based source row (header counted). For a grouped unit, the first data row.
  rowNo: number;
  // Validated payload, or null when the row(s) failed validation.
  data: T | null;
  // Validation message shown in the error list when `data` is null.
  error: string | null;
  // 'update' shows the amber "อัปเดต" badge; anything else counts as create.
  action?: 'create' | 'update';
}

export interface ImportResult {
  created: number;
  updated: number;
  failed: number;
  errors: { code: string; message: string }[];
}

// A column in the preview table (stage 'preview').
export interface PreviewColumn<T> {
  header: string;
  width?: number;
  render: (data: T) => ReactNode;
}

export interface SpreadsheetImportModalProps<T> {
  // Dialog header, e.g. "นำเข้าสินทรัพย์ถาวร".
  title: string;
  // Explanatory text on the pick stage. A sensible default is used if omitted.
  intro?: ReactNode;
  // Reference table rows (and, by default, the template's example layout).
  fields: ImportFieldSpec[];
  // Header row written to the downloadable template — keep these the SAME keys
  // the page's exporter writes so an exported file re-imports cleanly.
  templateColumns: string[];
  // Example data rows for the template (after the header).
  templateExamples: CellValue[][];
  // Template download filename, e.g. "fixed_asset_template.xlsx".
  templateFilename: string;
  // Worksheet name for the template (Thai is fine).
  sheetName?: string;
  // Parse an uploaded grid into validated units. `grid[0]` is the header row.
  parse: (grid: string[][]) => { units: ParsedUnit<T>[]; headerError: string | null };
  // Columns shown for each valid unit in the preview table.
  previewColumns: PreviewColumn<T>[];
  // Show the create-vs-update split (set true only for upsert-style imports).
  showUpdate?: boolean;
  // Import the valid units one by one; call onProgress(done,total) after each.
  runImport: (
    valid: ParsedUnit<T>[],
    onProgress: (done: number, total: number) => void,
  ) => Promise<ImportResult>;
  onClose: () => void;
}

type ImportStage = 'pick' | 'preview' | 'running' | 'done';

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
const box: CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  // Clip children (header border, footer fill) to the rounded corners.
  overflow: 'hidden',
  width: 920,
  maxWidth: '95vw',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};
const stat: CSSProperties = { fontSize: 13, color: 'var(--muted)' };

export function SpreadsheetImportModal<T>({
  title,
  intro,
  fields,
  templateColumns,
  templateExamples,
  templateFilename,
  sheetName,
  parse,
  previewColumns,
  showUpdate = false,
  runImport,
  onClose,
}: SpreadsheetImportModalProps<T>) {
  const fileRef = useRef<HTMLInputElement>(null);

  const [stage, setStage] = useState<ImportStage>('pick');
  const [fileName, setFileName] = useState('');
  const [units, setUnits] = useState<ParsedUnit<T>[]>([]);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<ImportResult | null>(null);

  const valid = useMemo(
    () => units.filter((u): u is ParsedUnit<T> & { data: T } => u.data !== null),
    [units],
  );
  const invalid = useMemo(() => units.filter((u) => u.data === null), [units]);
  const toUpdate = useMemo(() => valid.filter((u) => u.action === 'update'), [valid]);
  const toCreate = useMemo(() => valid.filter((u) => u.action !== 'update'), [valid]);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const { units: parsedUnits, headerError: hErr } = parse(await fileToGrid(file));
    setHeaderError(hErr);
    setUnits(parsedUnits);
    setStage('preview');
  }

  async function handleRun() {
    setStage('running');
    setProgress({ done: 0, total: valid.length });
    const res = await runImport(valid, (done, total) => setProgress({ done, total }));
    setResult(res);
    setStage('done');
  }

  return (
    <div style={overlay} onClick={stage === 'running' ? undefined : onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
        </div>

        <div style={{ padding: 18, display: 'grid', gap: 14 }}>
          {stage === 'pick' && (
            <>
              <div style={stat}>
                {intro ?? (
                  <>
                    เลือกไฟล์ Excel (.xlsx) หรือ CSV ที่มีหัวคอลัมน์ตามตารางด้านล่าง — แนะนำให้กด
                    “ดาวน์โหลดเทมเพลต” หรือ “ส่งออก” แล้วแก้ในไฟล์นั้นก่อนนำเข้า
                  </>
                )}
              </div>
              <div
                style={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                }}
              >
                <table
                  className="tbl"
                  style={{ fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: 120 }}>คอลัมน์</th>
                      <th style={{ width: 150 }}>ความหมาย</th>
                      <th style={{ width: 80 }}>จำเป็น</th>
                      <th>ค่าที่ใช้ได้ / ตัวอย่าง</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map((f) => (
                      <tr key={f.col}>
                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          <code style={{ fontSize: 12 }}>{f.col}</code>
                        </td>
                        <td style={{ whiteSpace: 'normal' }}>{f.label}</td>
                        <td>
                          {f.required ? (
                            <span className="badge warning">จำเป็น</span>
                          ) : (
                            <span className="muted">ไม่บังคับ</span>
                          )}
                        </td>
                        <td className="muted" style={{ whiteSpace: 'normal' }}>
                          {f.hint}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => void handleFile(e)}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn primary" onClick={() => fileRef.current?.click()}>
                  <Icon name="upload" size={14} /> เลือกไฟล์
                </button>
                <button
                  className="btn"
                  onClick={() =>
                    void downloadXlsx(
                      templateFilename,
                      [templateColumns, ...templateExamples],
                      sheetName ? { sheetName } : {},
                    )
                  }
                >
                  <Icon name="download" size={14} /> ดาวน์โหลดเทมเพลต
                </button>
              </div>
            </>
          )}

          {stage === 'preview' &&
            (headerError ? (
              <div
                style={{
                  background: 'var(--negative-bg)',
                  color: 'var(--negative)',
                  padding: '10px 12px',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {headerError}
              </div>
            ) : (
              <>
                <div style={stat}>
                  ไฟล์: <b>{fileName}</b> — พบ {units.length} แถว
                </div>
                <div style={{ display: 'flex', gap: 18, fontSize: 14 }}>
                  <span>
                    สร้างใหม่ <b style={{ color: 'var(--positive)' }}>{toCreate.length}</b>
                  </span>
                  {showUpdate && (
                    <span>
                      อัปเดต <b style={{ color: '#1a56a6' }}>{toUpdate.length}</b>
                    </span>
                  )}
                  <span>
                    ผิดพลาด <b style={{ color: 'var(--negative)' }}>{invalid.length}</b>
                  </span>
                </div>
                {valid.length > 0 && (
                  <div
                    style={{
                      maxHeight: 220,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                    }}
                  >
                    <table
                      className="tbl"
                      style={{ fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}
                    >
                      <thead>
                        <tr>
                          {previewColumns.map((c, i) => (
                            <th key={i} style={c.width ? { width: c.width } : undefined}>
                              {c.header}
                            </th>
                          ))}
                          {showUpdate && <th style={{ width: 90 }}>การกระทำ</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {valid.map((u) => (
                          <tr key={u.rowNo}>
                            {previewColumns.map((c, i) => (
                              <td key={i} style={{ whiteSpace: 'normal' }}>
                                {c.render(u.data)}
                              </td>
                            ))}
                            {showUpdate && (
                              <td>
                                {u.action === 'update' ? (
                                  <span className="badge neutral">อัปเดต</span>
                                ) : (
                                  <span className="badge positive">สร้างใหม่</span>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {invalid.length > 0 && (
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      wordBreak: 'break-word',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '6px 10px',
                      fontSize: 12.5,
                    }}
                  >
                    {invalid.map((u) => (
                      <div key={u.rowNo} style={{ color: 'var(--negative)' }}>
                        แถว {u.rowNo}: {u.error}
                      </div>
                    ))}
                  </div>
                )}
              </>
            ))}

          {stage === 'running' && (
            <div style={{ fontSize: 14 }}>
              กำลังนำเข้า… {progress.done}/{progress.total}
            </div>
          )}

          {stage === 'done' && result && (
            <>
              <div style={{ display: 'flex', gap: 18, fontSize: 14 }}>
                <span>
                  สร้าง <b style={{ color: 'var(--positive)' }}>{result.created}</b>
                </span>
                {showUpdate && (
                  <span>
                    อัปเดต <b style={{ color: '#1a56a6' }}>{result.updated}</b>
                  </span>
                )}
                <span>
                  ล้มเหลว <b style={{ color: 'var(--negative)' }}>{result.failed}</b>
                </span>
              </div>
              {result.errors.length > 0 && (
                <div
                  style={{
                    maxHeight: 160,
                    overflow: 'auto',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '6px 10px',
                    fontSize: 12.5,
                  }}
                >
                  {result.errors.map((e, i) => (
                    <div key={`${e.code}-${i}`} style={{ color: 'var(--negative)' }}>
                      {e.code}: {e.message}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          {stage === 'done' ? (
            <button className="btn primary" onClick={onClose}>
              เสร็จสิ้น
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn ghost"
                onClick={onClose}
                disabled={stage === 'running'}
              >
                ยกเลิก
              </button>
              {stage === 'preview' && !headerError && (
                <button
                  className="btn primary"
                  onClick={() => void handleRun()}
                  disabled={valid.length === 0}
                >
                  นำเข้า {valid.length} รายการ
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
